/**
 * MapLabelsOverlay - Renders area (building) and unit labels on the 3D map
 *
 * Performance-optimized: positions are updated imperatively via style.transform
 * in the shared RAF loop. React only re-renders when buildings or toggle states change.
 *
 * Area labels appear above each building (hangar name).
 * Unit labels appear at the center of each unit within a building.
 * Controlled by showAreaLabels and showUnitLabels in rampViewConfigStore.
 */

import { useEffect, useMemo, useRef, createRef } from 'react'
import { useRampViewConfigStore } from '@/stores/rampViewConfigStore'
import { useAircraftEditorStore } from '@/stores/aircraftEditorStore'
import type { Building3DConfigExtended } from '@/services/3d/BuildingLoaderService'
import type { LabelPositionData } from '@/hooks/useMapLabelPositions'

interface MapLabelsOverlayProps {
  buildings: Building3DConfigExtended[]
  onFrame: (callback: (positions: LabelPositionData) => void) => () => void
}

export function MapLabelsOverlay({ buildings, onFrame }: MapLabelsOverlayProps) {
  const showAreaLabels = useRampViewConfigStore((state) => state.showAreaLabels)
  const showUnitLabels = useRampViewConfigStore((state) => state.showUnitLabels)
  const isDragging = useAircraftEditorStore((state) => state.isDragging)
  const isRotating = useAircraftEditorStore((state) => state.isRotating)

  // Build stable lists for rendering
  const areaItems = useMemo(() => {
    if (!showAreaLabels) return []
    return buildings.map((b) => ({ id: b.id, name: b.name }))
  }, [buildings, showAreaLabels])

  const unitItems = useMemo(() => {
    if (!showUnitLabels) return []
    const items: { key: string; unitCode: string }[] = []
    for (const bld of buildings) {
      const units = bld.generatedUnits
      if (!units || units.length === 0) continue
      for (const unit of units) {
        items.push({ key: `${bld.id}:${unit.code}`, unitCode: unit.code })
      }
    }
    return items
  }, [buildings, showUnitLabels])

  // Per-label DOM refs for imperative positioning
  const areaRefsMap = useRef<Map<string, React.RefObject<HTMLDivElement | null>>>(new Map())
  const unitRefsMap = useRef<Map<string, React.RefObject<HTMLDivElement | null>>>(new Map())

  // Sync area refs
  useMemo(() => {
    const newMap = new Map<string, React.RefObject<HTMLDivElement | null>>()
    for (const item of areaItems) {
      newMap.set(item.id, areaRefsMap.current.get(item.id) || createRef<HTMLDivElement>())
    }
    areaRefsMap.current = newMap
  }, [areaItems])

  // Sync unit refs
  useMemo(() => {
    const newMap = new Map<string, React.RefObject<HTMLDivElement | null>>()
    for (const item of unitItems) {
      newMap.set(item.key, unitRefsMap.current.get(item.key) || createRef<HTMLDivElement>())
    }
    unitRefsMap.current = newMap
  }, [unitItems])

  // Subscribe to the shared RAF position loop
  useEffect(() => {
    if (areaItems.length === 0 && unitItems.length === 0) return

    const unsubscribe = onFrame((positions: LabelPositionData) => {
      // Update area labels
      for (const [id, ref] of areaRefsMap.current) {
        const el = ref.current
        if (!el) continue
        const pos = positions.areas.get(id)
        if (!pos || !pos.visible) {
          el.style.transform = 'translate3d(-9999px, -9999px, 0)'
          continue
        }
        el.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -100%)`
      }

      // Update unit labels
      for (const [key, ref] of unitRefsMap.current) {
        const el = ref.current
        if (!el) continue
        const pos = positions.units.get(key)
        if (!pos || !pos.visible) {
          el.style.transform = 'translate3d(-9999px, -9999px, 0)'
          continue
        }
        el.style.transform = `translate3d(${pos.x}px, ${pos.y}px, 0) translate(-50%, -50%)`
      }
    })

    return unsubscribe
  }, [onFrame, areaItems.length, unitItems.length])

  // Early exit if nothing to render
  if (!showAreaLabels && !showUnitLabels) {
    return null
  }

  const isInteracting = isDragging || isRotating

  return (
    <div
      className={`absolute inset-0 z-10 pointer-events-none overflow-hidden transition-opacity duration-150 ${
        isInteracting ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Area labels (building names) */}
      {showAreaLabels &&
        areaItems.map((item) => (
          <div
            key={`area-${item.id}`}
            ref={areaRefsMap.current.get(item.id)}
            className="absolute left-0 top-0"
            style={{
              contain: 'layout style paint',
              willChange: 'transform',
              transform: 'translate3d(-9999px, -9999px, 0)',
            }}
          >
            <div className="px-2 py-0.5 bg-primary/90 text-primary-foreground rounded shadow-sm text-xs font-medium whitespace-nowrap">
              {item.name}
            </div>
          </div>
        ))}

      {/* Unit labels (unit codes) */}
      {showUnitLabels &&
        unitItems.map((item) => (
          <div
            key={`unit-${item.key}`}
            ref={unitRefsMap.current.get(item.key)}
            className="absolute left-0 top-0"
            style={{
              contain: 'layout style paint',
              willChange: 'transform',
              transform: 'translate3d(-9999px, -9999px, 0)',
            }}
          >
            <div className="px-1.5 py-0.5 bg-muted/80 text-muted-foreground rounded shadow-sm text-[10px] font-mono whitespace-nowrap">
              {item.unitCode}
            </div>
          </div>
        ))}
    </div>
  )
}
